import { Injectable } from '@angular/core';

import { SentryLoggerService, SentryLogContext, SentryLogOptions } from 'src/app/services/sentry-logger.service';
import { CollaborationTokenStoreService } from './collaboration-token-store.service';

const SESSION_ID_KEY = 'collaboration_session_id';

/**
 * Collaboration-scoped logger that wraps SentryLoggerService and automatically
 * enriches every log entry with collaboration context (sessionId, companyId,
 * collaboratorEmail, assessmentId, current URL) so logs can be grouped and
 * traced per session without manual boilerplate in each call site.
 */
@Injectable({
    providedIn: 'root',
})
export class CollaborationLoggerService {
    private readonly sessionId: string;

    constructor(
        private sentryLogger: SentryLoggerService,
        private tokenStore: CollaborationTokenStoreService,
    ) {
        this.sessionId = this.initSessionId();
    }

    info(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.sentryLogger.info(message, this.enrich(context), this.withFeature(options));
    }

    warn(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.sentryLogger.warn(message, this.enrich(context), this.withFeature(options));
    }

    error(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.sentryLogger.error(message, this.enrich(context), this.withFeature(options));
    }

    fatal(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.sentryLogger.fatal(message, this.enrich(context), this.withFeature(options));
    }

    debug(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.sentryLogger.debug(message, this.enrich(context), this.withFeature(options));
    }

    logError(error: Error | string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.sentryLogger.logError(error, this.enrich(context), this.withFeature(options));
    }

    getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Merges per-call context on top of the automatically collected collaboration
     * context. Caller-supplied values take precedence, so an explicit assessmentId
     * passed by the caller overrides the one pulled from the store.
     */
    private enrich(context?: SentryLogContext): SentryLogContext {
        return {
            sessionId: this.sessionId,
            url: window.location.href,
            companyId: this.tokenStore.getCompanyId() ?? null,
            collaboratorEmail: this.tokenStore.getCollaboratorEmail() ?? null,
            assessmentId: this.tokenStore.getActiveAssessmentId() ?? null,
            ...context,
        };
    }

    /** Ensures `feature: 'collaboration'` is always present, unless overridden. */
    private withFeature(options?: SentryLogOptions): SentryLogOptions {
        return { feature: 'collaboration', ...options };
    }

    private initSessionId(): string {
        let id = sessionStorage.getItem(SESSION_ID_KEY);
        if (!id) {
            id = this.generateUUID();
            sessionStorage.setItem(SESSION_ID_KEY, id);
        }
        return id;
    }

    private generateUUID(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        // Fallback for environments without crypto.randomUUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}
